// Shortbread vector tile basemap (VersaTiles), rendered client-side. Real tiles only go up to
// z14 (see NATIVE_MAX_ZOOM); above that the map overzooms a sub-square of the z14 parent tile
// (vector data, so it stays sharp — only line widths don't scale with the overzoom).
//
// Rendering happens in a module worker (fetch -> decode -> draw into an OffscreenCanvas ->
// transferToImageBitmap) when available, so decoding/drawing never blocks the main thread; falls
// back to synchronous main-thread rendering (regular <canvas> + createImageBitmap) otherwise
// (e.g. iOS Safari < 16.4, or any browser without OffscreenCanvas 2D support).

import type { TileSource } from './tiles';
import { drawShortbreadTile } from './vectorStyle';
import { fetchAndDecode, overzoomCrop } from './vectorTileDecode';

export const NATIVE_MAX_ZOOM = 14;
export const OUTPUT_SIZE = 512;

export const SHORTBREAD_ATTRIBUTION = '© OpenStreetMap contributors, VersaTiles';

function tileUrl(z: number, x: number, y: number): string {
  return `https://tiles.versatiles.org/tiles/osm/${z}/${x}/${y}`;
}

/** Renders one tile on the calling thread (used by the fallback, and directly by tests). */
async function renderTileMainThread(z: number, x: number, y: number, signal: AbortSignal): Promise<ImageBitmap> {
  const crop = overzoomCrop(z, x, y, NATIVE_MAX_ZOOM);
  const decoded = await fetchAndDecode(crop.nz, crop.nx, crop.ny, signal);
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  drawShortbreadTile(ctx, decoded.layers, {
    size: OUTPUT_SIZE,
    extent: decoded.extent,
    zoom: z,
    crop: { x0: crop.fx0 * decoded.extent, y0: crop.fy0 * decoded.extent, size: crop.fsize * decoded.extent },
  });
  return createImageBitmap(canvas);
}

function supportsOffscreenCanvas2D(): boolean {
  if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined') return false;
  try {
    return new OffscreenCanvas(1, 1).getContext('2d') !== null;
  } catch {
    return false;
  }
}

interface WorkerOkMsg {
  id: number;
  ok: true;
  bitmap: ImageBitmap;
}
interface WorkerErrMsg {
  id: number;
  ok: false;
  error: string;
}

/** Routes tile render requests to a worker (request ids + cancel messages for aborts). */
class WorkerRenderer {
  private readonly worker: Worker;
  private nextId = 1;
  private readonly pending = new Map<number, { resolve: (b: ImageBitmap) => void; reject: (e: Error) => void }>();

  constructor() {
    this.worker = new Worker(new URL('./vectorTile.worker.ts', import.meta.url), { type: 'module' });
    this.worker.addEventListener('message', (ev: MessageEvent) => {
      const msg = ev.data as WorkerOkMsg | WorkerErrMsg;
      const p = this.pending.get(msg.id);
      if (!p) return;
      this.pending.delete(msg.id);
      if (msg.ok) p.resolve(msg.bitmap);
      else p.reject(new Error(msg.error));
    });
  }

  render(z: number, x: number, y: number, signal: AbortSignal): Promise<ImageBitmap> {
    const id = this.nextId++;
    return new Promise<ImageBitmap>((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('aborted', 'AbortError'));
        return;
      }
      this.pending.set(id, { resolve, reject });
      const onAbort = (): void => {
        this.worker.postMessage({ cancel: id });
        this.pending.delete(id);
        reject(new DOMException('aborted', 'AbortError'));
      };
      signal.addEventListener('abort', onAbort, { once: true });
      this.worker.postMessage({ id, z, x, y });
    });
  }
}

/** Factory: creates a fresh Shortbread vector tile source (own worker, own decode caches). */
export function createShortbreadSource(): TileSource {
  const renderer = supportsOffscreenCanvas2D() ? new WorkerRenderer() : null;
  return {
    url: tileUrl,
    maxNativeZoom: 18,
    tileSize: OUTPUT_SIZE,
    attribution: SHORTBREAD_ATTRIBUTION,
    load: (z, x, y, signal) => (renderer ? renderer.render(z, x, y, signal) : renderTileMainThread(z, x, y, signal)),
  };
}
